/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Chip, useTheme } from '@mui/material';
import React, { useRef } from 'react';

export interface FilterChipProps {
  id: string;
  label: React.ReactNode;
  isSelected?: boolean;
  isDisabled?: boolean;
  handleClick?: (label: string, isSelected?: boolean) => void;
  actionIcon?: React.ReactElement;
  onClick?: (ref: HTMLElement | null, event: React.MouseEvent) => void;
  onClickIcon?: (ref: HTMLElement | null, event: React.MouseEvent) => void;
  size?: 'small' | 'medium';
}

const FilterChip: React.FC<FilterChipProps> = ({
  label,
  id,
  isSelected = false,
  isDisabled = false,
  handleClick = (): void => {},
  onClick,
  onClickIcon,
  actionIcon,
  size = 'small',
}: FilterChipProps) => {
  const theme = useTheme();

  const chipRef = useRef<HTMLDivElement>(null);

  const clickHandler = (event: React.MouseEvent): void =>
    onClick ? onClick(chipRef.current, event) : handleClick(id, isSelected);

  const clickIconHandler = (event: React.MouseEvent): void =>
    onClickIcon?.(chipRef.current, event);

  return (
    <Chip
      color={isSelected ? 'secondary' : 'default'}
      deleteIcon={actionIcon}
      disabled={isDisabled}
      id={id}
      label={label}
      onClick={clickHandler}
      onDelete={onClickIcon ? clickIconHandler : undefined}
      ref={chipRef}
      size={size}
      sx={{
        borderColor: isDisabled
          ? 'none'
          : theme.palette.geowebColors.buttons.tertiary.default.borderColor,
        padding: '0px 4px',
        '& .MuiSvgIcon-root, .MuiChip-deleteIcon': {
          fill: isSelected
            ? theme.palette.geowebColors.buttons.secondary.active.color!
            : theme.palette.geowebColors.buttons.flat.default.color!,
          color: isSelected
            ? theme.palette.geowebColors.buttons.secondary.active.color!
            : theme.palette.geowebColors.buttons.flat.default.color!,
        },
        '& .MuiChip-filled': {
          border: '1px solid transparent',
        },
        '&.MuiChip-sizeMedium > .MuiChip-label': {
          overflow: 'visible',
        },
        '&.Mui-disabled': {
          borderColor: 'transparent',
        },
      }}
      variant={isSelected ? 'filled' : 'outlined'} // Chip does not have a selected state so theme class cannot be used for styling
    />
  );
};

export default FilterChip;
